import { QueryFunction } from '@tanstack/react-query';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface GetAiPerformanceParams {
  loan?: string; // loan number or internal id
  sourceFileName?: string;
  processedFileName?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
  cursor?: string;
  limit?: number;
}

/** Nested borrower metadata returned by AI performance API. */
export interface ValidatedBorrowerMetadataPayload {
  borrower?: {
    id?: string;
    fullName?: string;
    lastName?: string;
    firstName?: string;
  };
  coborrower?: { id?: string };
}

/** Row type for AI Performance report response. */
export interface AiPerformanceRow {
  documentId: string;
  loanId: string;
  loanNumber: string;
  loanCreatedAt: string | null;
  sourceFileName: string | null;
  docPullAt: string | null;
  sourcePageCount: number | null;
  processedFileName: string | null;
  processedPageCount: number | null;
  processingCompletedAt: string | null;
  documentUpdatedAt: string | null;
  assignedBaseClass: string | null;
  aiAssignedMosClass: string | null;
  classificationConfidence: number | null;
  classificationReviewThreshold: number | null;
  validatedMosClass: string | null;
  borrowerScope: string | null;
  validatedBorrowerPairNumber: number | null;
  validatedBorrowerMetadata: ValidatedBorrowerMetadataPayload | string | null;
  aiAssignedBorrowerDisplay: string | null;
  isUserTaggedBorrower: boolean;
  extractionFieldName: string | null;
  aiExtractedValue: string | null;
  aiExtractionConfidence: number | null;
  extractionReviewThreshold: number | null;
  validatedExtractionValue: string | null;
  versionIdentifier: string | null;
  versionCustomName: string | null;
  manuallyCorrected: boolean;
  extractedDataId: string | null;
}

export interface AiPerformanceMeta {
  nextCursor: string | null;
}

export interface AiPerformanceData {
  rows: AiPerformanceRow[];
  meta: AiPerformanceMeta;
}

export interface GetAiPerformanceResponse {
  data: AiPerformanceData;
}

/** Backend may return { data: array, meta?: { nextCursor, count } } */
interface RawApiResponse {
  data: unknown;
  meta?: { nextCursor?: string | null; count?: number };
}

const buildUrlParams = (
  params: GetAiPerformanceParams,
  cursor: string | undefined,
  limit: number,
): URLSearchParams => {
  const urlParams = new URLSearchParams();
  if (params.loan?.trim()) {
    urlParams.append('loan', params.loan.trim());
  }
  if (params.sourceFileName?.trim()) {
    urlParams.append('sourceFileName', params.sourceFileName.trim());
  }
  if (params.processedFileName?.trim()) {
    urlParams.append('processedFileName', params.processedFileName.trim());
  }
  if (params.dateFrom) {
    urlParams.append('dateFrom', params.dateFrom);
  }
  if (params.dateTo) {
    urlParams.append('dateTo', params.dateTo);
  }
  if (cursor) {
    urlParams.append('cursor', cursor);
  }
  urlParams.append('limit', String(limit));
  return urlParams;
};

export const fetchAiPerformance = async (
  params: GetAiPerformanceParams,
): Promise<GetAiPerformanceResponse> => {
  const cursor = params.cursor;
  const limit = params.limit ?? 50;

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const urlParams = buildUrlParams(params, cursor, limit);
  // Path only (no tenant): buildApiUrl applies routing strategy for tenant/header values.
  const basePath = '/reports/ai-performance';
  const url = `${basePath}?${urlParams.toString()}`;

  const response = await authTokenHttp<
    GetAiPerformanceResponse | RawApiResponse
  >(url, {
    method: 'GET',
  });

  const body = response.data;
  if (!body) {
    throw new Error('Failed to fetch AI Performance data');
  }

  // Backend may return { data: rows[], meta?: { nextCursor, count } }.
  // Normalize to { data: { rows, meta: { nextCursor } } }.
  const raw = body as RawApiResponse;
  if (Array.isArray(raw.data)) {
    return {
      data: {
        rows: raw.data as AiPerformanceRow[],
        meta: { nextCursor: raw.meta?.nextCursor ?? null },
      },
    };
  }

  if (!(body as GetAiPerformanceResponse).data?.rows) {
    throw new Error('Failed to fetch AI Performance data');
  }
  return body as GetAiPerformanceResponse;
};

const getAiPerformance: QueryFunction<
  GetAiPerformanceResponse,
  ['/reports/ai-performance', GetAiPerformanceParams]
> = async ({ queryKey }) => {
  const [, params] = queryKey;
  return fetchAiPerformance(params);
};

export default getAiPerformance;
